/**
 * API Client Core Module
 * Handles axios instance creation, interceptors, and token refresh logic
 * Part of API optimization - follows AIM atomicity principle
 */

import axios from 'axios'
import { toast } from '../toastService'
import { createRetryInterceptor } from '../apiClient'
import i18n from '../../i18n'

// Create axios instance
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Enable sending cookies
})

// Add retry logic for transient failures (before auth interceptor)
createRetryInterceptor(api)

// Token refresh state management
let isRefreshing = false
let refreshSubscribers = []
let refreshAttempts = 0
const MAX_REFRESH_ATTEMPTS = 3
const REFRESH_RETRY_DELAY = 1000 // Start with 1 second

// Subscribe to token refresh completion
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback)
}

// Notify all subscribers when token is refreshed
const onTokenRefreshed = () => {
  refreshSubscribers.forEach((callback) => callback())
  refreshSubscribers = []
}

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config
    
    // Silently handle expected 401s (like initial auth checks)
    if (error.response?.status === 401 && originalRequest.silentAuth) {
      error.config.__suppressLog = true
      return Promise.reject(error)
    }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      // Skip token refresh for initial profile check and refresh attempts
      if (originalRequest.url?.includes('/profile') || originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error)
      }
      
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh(() => {
            resolve(api(originalRequest))
          })
        })
      }
      
      isRefreshing = true
      
      try {
        // Check if we've exceeded max attempts
        if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
          throw new Error('Max refresh attempts exceeded')
        }
        
        refreshAttempts++
        
        // Exponential backoff: 1s, 2s, 4s
        if (refreshAttempts > 1) {
          const delay = REFRESH_RETRY_DELAY * Math.pow(2, refreshAttempts - 1)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
        
        // Refresh token is sent automatically via httpOnly cookie
        await api.post('/auth/refresh', {}, { timeout: 5000 })
        
        // Reset attempts on success
        refreshAttempts = 0
        
        // Notify subscribers and retry original request
        onTokenRefreshed()
        isRefreshing = false
        
        return api(originalRequest)
        
      } catch (refreshError) {
        isRefreshing = false
        refreshAttempts = 0
        refreshSubscribers = []
        
        return Promise.reject(refreshError)
      }
    }
    
    // Handle different error types
    if (error.response) {
      const { status, data } = error.response
      const apiError = data.error
      const message = apiError?.message || data.message || data.error
      
      const isSilentAuth = error.config?.silentAuth
      const isProfileCheck = error.config?.url?.includes('/profile')
      
      switch (status) {
        case 400:
          if (error.config?.silentError) break
          if (apiError?.fields && apiError.fields.length > 0) {
            apiError.fields.forEach(field => {
              toast.error(`${field.field}: ${field.message}`)
            })
          } else if (!isProfileCheck) {
            toast.error(message || 'Bad request')
          }
          break
        case 401:
          if (!isSilentAuth && !isProfileCheck) {
            toast.error(message || 'Please login to continue')
          }
          break
        case 403:
          if (!isProfileCheck) {
            toast.error(message || 'You are not authorized to perform this action')
          }
          break
        case 404:
          if (!isProfileCheck) {
            toast.error(message || 'Resource not found')
          }
          break
        case 409:
          toast.error(message || 'Resource already exists')
          break
        case 429:
          toast.error(i18n.t('toast.error.tooManyRequests'))
          break
        case 500:
          if (!isProfileCheck && !error.config?.silentError) {
            toast.error(i18n.t('toast.error.serverError'))
          }
          break
        default:
          if (!isProfileCheck && !error.config?.silentError) {
            toast.error(message || i18n.t('toast.error.unexpectedError'))
          }
      }
    } else if (error.request) {
      if (!error.config?.silentError) {
        toast.error(i18n.t('toast.error.networkError'))
      }
    } else {
      if (!error.config?.silentError) {
        toast.error(i18n.t('toast.error.unexpectedError'))
      }
    }
    
    return Promise.reject(error)
  }
)

export default api
